import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, map, Observable, ReplaySubject } from 'rxjs';
import { DocumentType } from 'src/app/compliance/models/document-type';
import { APP_CONFIG, Settings } from 'src/app/config/settings';
import { DocumentTypeSummaryBase } from 'src/app/shared/lib/compliance-shared/models/document-type-summary-base.model';
import { FetchArgs, PagingTokenFixed } from 'src/app/shared/models/core';
import { AcceptanceCriteriaStatus } from 'src/app/shared/models/enums/acceptance-criteria-status.enum';
import { AcceptanceCriteriaType } from 'src/app/shared/models/enums/acceptance-criteria-type.enum';
import { LineOfBusiness } from 'src/app/shared/models/enums/line-of-business';
import { KeyValue } from 'src/app/shared/models/key-value.model';
import { LDFeatureManager } from 'src/app/shared/services/ld-feature-manager';
import { buildHttpParams, interpolateParams } from 'src/app/shared/utilities/interpolate-params';
import { combine } from 'src/app/shared/utilities/route';
import { CategoryLineOfBusiness } from '../enums/category-line-of-business.enum';
import { EsignTemplate } from '../models/doc-admin-esign-template.model';
import { DocAdminPermissions } from '../models/doc-admin-permissions.model';
import { DocumentTypeCategory } from '../models/document-type-category.model';
import { DocumentTypesCreate } from '../models/document-types-create.model';
import { DocumentTypesUpdate } from '../models/document-types-update.model';
import { DocmentTypeFile, DocumentTypes } from '../models/document-types.model';
import { ProfessionTree } from '../models/profession-tree.model';
import { selectProfessionTree } from '../store/selectors/profession-specialty-configuration.selector';

/**
 * @deprecated Do not use this service.  Use service(s) from "src/app/compliance/api" or "src/app/compliance/api/migration"
 */
@Injectable({ providedIn: 'root' })
export class DocAdminService {
    private readonly coreApiAdminPath: string;
    private readonly coreApiDocTypesPath: string;

    private currentStates$: ReplaySubject<string[]> = new ReplaySubject<string[]>(1);
    private currentSnapshotStates$: ReplaySubject<string[]> = new ReplaySubject<string[]>(1);

    private readonly currentFacilityTypes$: ReplaySubject<string[]> = new ReplaySubject<string[]>(1);
    private readonly currentSnapshotFacilityTypes$: ReplaySubject<string[]> = new ReplaySubject<string[]>(1);

    private currentProfessionAndSpecialtySettings$: ReplaySubject<string[]> = new ReplaySubject<string[]>(1);
    private currentSnapShotProfessionAndSpecialtySettings$: ReplaySubject<string[]> = new ReplaySubject<string[]>(1);

    private currentFacilityLinesOfBusiness$: ReplaySubject<KeyValue<number, KeyValue<string, boolean>>> =
        new ReplaySubject<KeyValue<number, KeyValue<string, boolean>>>(1);
    private currentSnapshotFacilityLinesOfBusiness$: ReplaySubject<KeyValue<number, KeyValue<string, boolean>>> =
        new ReplaySubject<KeyValue<number, KeyValue<string, boolean>>>(1);

    private currentSampleFile$: ReplaySubject<File> = new ReplaySubject<File>(1);
    private currentSampleBlob$: ReplaySubject<Blob> = new ReplaySubject<Blob>(1);

    private existingDocuSignTemplates$: BehaviorSubject<EsignTemplate[]> = new BehaviorSubject<EsignTemplate[]>(
        undefined
    );
    private selectedDocuSignTemplates$: BehaviorSubject<EsignTemplate[]> = new BehaviorSubject<EsignTemplate[]>(
        undefined
    );

    private snapshotUpdated$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    private currentExternalInstructions$: BehaviorSubject<string> = new BehaviorSubject<string>(undefined);

    isNoStatesOrSpecialties$: Observable<boolean> = combineLatest({
        availableProfessions: this.store.select(selectProfessionTree).pipe(map((tree) => tree?.professionTypes || [])),
        currentProfessionsAndSpecialties: this.currentProfessionAndSpecialtySettings$,
        currentLinesOfBusiness: this.currentFacilityLinesOfBusiness$,
        currentStates: this.currentStates$
    }).pipe(
        map(({ availableProfessions, currentProfessionsAndSpecialties, currentLinesOfBusiness, currentStates }) => {
            if (
                !currentProfessionsAndSpecialties ||
                !currentLinesOfBusiness ||
                !currentStates ||
                !availableProfessions
            ) {
                return false;
            }
            const hasNoSpecialties =
                (currentProfessionsAndSpecialties.filter((s) => s.startsWith('T')).length ===
                    availableProfessions.length ||
                    currentProfessionsAndSpecialties.filter((s) => s.startsWith('T')).length === 0) &&
                currentProfessionsAndSpecialties.filter((s) => s.startsWith('P')).length === 0 &&
                currentProfessionsAndSpecialties.filter((s) => s.startsWith('S')).length === 0;
            const hasNoLinesOfBusiness =
                Object.entries(currentLinesOfBusiness).some(
                    (lob) => lob[1].direct === true || lob[1].subcontractor === true
                ) === false;
            const hasNoStates = currentStates.length === 0;
            return hasNoSpecialties && hasNoLinesOfBusiness && hasNoStates;
        })
    );

    isNoStatesOrSpecialtiesSnapshot$: Observable<boolean> = combineLatest({
        availableProfessions: this.store.select(selectProfessionTree).pipe(map((tree) => tree?.professionTypes || [])),
        currentProfessionsAndSpecialties: this.currentSnapShotProfessionAndSpecialtySettings$,
        currentLinesOfBusiness: this.currentSnapshotFacilityLinesOfBusiness$,
        currentStates: this.currentSnapshotStates$
    }).pipe(
        map(({ availableProfessions, currentProfessionsAndSpecialties, currentLinesOfBusiness, currentStates }) => {
            if (
                !currentProfessionsAndSpecialties ||
                !currentLinesOfBusiness ||
                !currentStates ||
                !availableProfessions
            ) {
                return false;
            }
            const hasNoSpecialties =
                (currentProfessionsAndSpecialties.filter((s) => s.startsWith('T')).length ===
                    availableProfessions.length ||
                    currentProfessionsAndSpecialties.filter((s) => s.startsWith('T')).length === 0) &&
                currentProfessionsAndSpecialties.filter((s) => s.startsWith('P')).length === 0 &&
                currentProfessionsAndSpecialties.filter((s) => s.startsWith('S')).length === 0;
            const hasNoLinesOfBusiness =
                Object.entries(currentLinesOfBusiness).some(
                    (lob) => lob[1].direct === true || lob[1].subcontractor === true
                ) === false;
            const hasNoStates = currentStates.length === 0;
            return hasNoSpecialties && hasNoLinesOfBusiness && hasNoStates;
        })
    );

    constructor(
        private readonly http: HttpClient,
        @Inject(APP_CONFIG) readonly settings: Settings,
        private readonly store: Store<{}>,
        private readonly _ldManager: LDFeatureManager
    ) {
        this.coreApiAdminPath = combine(settings.coreAPIUrl, 'ayanova', 'compliance', 'document-admin');
        this.coreApiDocTypesPath = combine(settings.coreAPIUrl, 'ayanova', 'compliance', 'document-types');
    }

    get CurrentStates$(): ReplaySubject<string[]> {
        return this.currentStates$;
    }

    get CurrentSnapshotStates$(): ReplaySubject<string[]> {
        return this.currentSnapshotStates$;
    }

    get CurrentFacilityTypes$(): ReplaySubject<string[]> {
        return this.currentFacilityTypes$;
    }

    get CurrentSnapshotFacilityTypes$(): ReplaySubject<string[]> {
        return this.currentSnapshotFacilityTypes$;
    }

    get CurrentProfessionAndSpecialtySettings$(): ReplaySubject<string[]> {
        return this.currentProfessionAndSpecialtySettings$;
    }

    get SnapshotProfessionAndSpecialtySettings$(): ReplaySubject<string[]> {
        return this.currentSnapShotProfessionAndSpecialtySettings$;
    }

    get CurrentFacilityLinesOfBusiness$(): ReplaySubject<KeyValue<number, KeyValue<string, boolean>>> {
        return this.currentFacilityLinesOfBusiness$;
    }

    get CurrentSnapshotFacilityLinesOfBusiness$(): ReplaySubject<KeyValue<number, KeyValue<string, boolean>>> {
        return this.currentSnapshotFacilityLinesOfBusiness$;
    }

    get CurrentSampleFile$(): ReplaySubject<File> {
        return this.currentSampleFile$;
    }

    get CurrentSampleBlob$(): ReplaySubject<Blob> {
        return this.currentSampleBlob$;
    }

    get ExistingDocuSignTemplates$(): BehaviorSubject<EsignTemplate[]> {
        return this.existingDocuSignTemplates$;
    }

    get SelectedDocuSignTemplates$(): BehaviorSubject<EsignTemplate[]> {
        return this.selectedDocuSignTemplates$;
    }

    get SnapshotUpdated$(): BehaviorSubject<boolean> {
        return this.snapshotUpdated$;
    }

    get CurrentExternalInstructions$(): BehaviorSubject<string> {
        return this.currentExternalInstructions$;
    }

    get AcceptanceCriteriaTypeOptions(): KeyValue<number, string>[] {
        return [
            {
                id: AcceptanceCriteriaType.None,
                key: AcceptanceCriteriaType.None,
                name: AcceptanceCriteriaType.toName(AcceptanceCriteriaType.None)
            },
            {
                id: AcceptanceCriteriaType.AcceptanceCriteriaNotAvailable,
                key: AcceptanceCriteriaType.AcceptanceCriteriaNotAvailable,
                name: AcceptanceCriteriaType.toName(AcceptanceCriteriaType.AcceptanceCriteriaNotAvailable)
            },
            {
                id: AcceptanceCriteriaType.LowestLevel,
                key: AcceptanceCriteriaType.LowestLevel,
                name: AcceptanceCriteriaType.toName(AcceptanceCriteriaType.LowestLevel)
            },
            {
                id: AcceptanceCriteriaType.CommonAcceptanceCriteria,
                key: AcceptanceCriteriaType.CommonAcceptanceCriteria,
                name: AcceptanceCriteriaType.toName(AcceptanceCriteriaType.CommonAcceptanceCriteria)
            }
        ];
    }

    get AcceptanceCriteriaStatusOptions(): KeyValue<number, string>[] {
        return [
            {
                id: AcceptanceCriteriaStatus.NotReviewed,
                key: AcceptanceCriteriaStatus.NotReviewed,
                name: AcceptanceCriteriaStatus.toName(AcceptanceCriteriaStatus.NotReviewed)
            },
            {
                id: AcceptanceCriteriaStatus.ReviewedNotNeeded,
                key: AcceptanceCriteriaStatus.ReviewedNotNeeded,
                name: AcceptanceCriteriaStatus.toName(AcceptanceCriteriaStatus.ReviewedNotNeeded)
            },
            {
                id: AcceptanceCriteriaStatus.ReviewedEnabled,
                key: AcceptanceCriteriaStatus.ReviewedEnabled,
                name: AcceptanceCriteriaStatus.toName(AcceptanceCriteriaStatus.ReviewedEnabled)
            },
            {
                id: AcceptanceCriteriaStatus.ReviewedManualNeeded,
                key: AcceptanceCriteriaStatus.ReviewedManualNeeded,
                name: AcceptanceCriteriaStatus.toName(AcceptanceCriteriaStatus.ReviewedManualNeeded)
            }
        ];
    }

    setCurrentStates(value: string[]): void {
        this.currentStates$.next(value);
    }

    setCurrentSnapshotStates(value: string[]): void {
        this.currentSnapshotStates$.next(value);
    }

    setCurrentFacilityTypes(value: string[]): void {
        this.currentFacilityTypes$.next(value);
    }

    setCurrentSnapshotFacilityTypes(value: string[]): void {
        this.currentSnapshotFacilityTypes$.next(value);
    }

    setCurrentProfessionAndSpecialtySettings(value: string[]): void {
        this.currentProfessionAndSpecialtySettings$.next(value);
    }

    setCurrentSnapShotProfessionAndSpecialtySettings(value: string[]): void {
        this.currentSnapShotProfessionAndSpecialtySettings$.next(value);
    }

    setCurrentFacilityLinesOfBusiness(value: KeyValue<number, KeyValue<string, boolean>>): void {
        this.currentFacilityLinesOfBusiness$.next(value);
    }

    setCurrentSnapshotFacilityLinesOfBusiness(value: KeyValue<number, KeyValue<string, boolean>>): void {
        this.currentSnapshotFacilityLinesOfBusiness$.next(value);
    }

    setCurrentSampleFile(file: File): void {
        this.currentSampleFile$.next(file);
    }

    setCurrentSampleBlob(blob: Blob): void {
        this.currentSampleBlob$.next(blob);
    }

    setExistingDocuSignTemplates(templates: EsignTemplate[]): void {
        this.existingDocuSignTemplates$.next(templates);
    }

    setSelectedDocuSignTemplates(templates: EsignTemplate[]): void {
        this.selectedDocuSignTemplates$.next(templates);
    }

    setSnapshotUpdated(isUpdated: boolean): void {
        this.snapshotUpdated$.next(isUpdated);
    }

    setCurrentExternalInstructions(externalInstructions: string): void {
        this.currentExternalInstructions$.next(externalInstructions);
    }

    // ** Does not exist.
    getPermissions(): Observable<DocAdminPermissions> {
        const url = `${this.coreApiAdminPath}/permissions`;
        return this.http.get<DocAdminPermissions>(url);
    }

    // ** DocumentAdminController.GetFacilityLookups
    getFacilityLookups(name: string, categoryLob: number): Observable<KeyValue<number, string>[]> {
        const url = `${this.coreApiAdminPath}/${categoryLob}/facility-lookups`;
        return this.http.get<KeyValue<number, string>[]>(url, {
            params: {
                name
            }
        });
    }

    // ** DocumentAdminController.GetAssociationLookups
    getAssociationLookups(name: string): Observable<KeyValue<number, string>[]> {
        const url = `${this.coreApiAdminPath}/association-lookups`;

        return this.http.get<KeyValue<number, string>[]>(url, {
            params: {
                name
            }
        });
    }

    // ** DocumentAdminController.UploadDocumentFile
    uploadDocumentFile(documentFile: DocmentTypeFile, documentId: number): Observable<DocumentTypes> {
        const url = `${this.coreApiAdminPath}/${documentId}/document`;
        const formData = new FormData();

        formData.append('document', documentFile.document);

        return this.http.post<DocumentTypes>(url, formData);
    }

    // ** DocumentAdminController.UploadDocument
    uploadDocument(document: DocumentTypesCreate): Observable<DocumentTypes> {
        const url = `${this.coreApiAdminPath}`;

        return this.http.post<DocumentTypes>(url, document);
    }

    // ** DocumentAdminController.UpdateDocument
    updateDocument(document: DocumentTypesUpdate): Observable<DocumentTypes> {
        const url = `${this.coreApiAdminPath}`;

        return this.http.put<DocumentTypes>(url, document);
    }

    // ** DocumentAdminController.GetCandidateTaskTypes
    getCandidateTaskTypes(includeDefault?: boolean): Observable<KeyValue<number, string>[]> {
        includeDefault = includeDefault ?? false;
        let url = `${this.coreApiAdminPath}/candidate-task-types`;
        url += `?includeDefault=${includeDefault}`;
        return this.http.get<KeyValue<number, string>[]>(url);
    }

    // ** DocumentAdminController.GetPartnerNetworkTaskTypes
    getPartnerNetworkTaskTypes(): Observable<KeyValue<number, string>[]> {
        const url = `${this.coreApiAdminPath}/partner-network-task-types`;

        return this.http.get<KeyValue<number, string>[]>(url);
    }

    // ** DocumentAdminController.GetSpecialties
    getSpecialties(getLocums: boolean): Observable<KeyValue<number, string>[]> {
        const url = `${this.coreApiAdminPath}/specialties`;
        return this.http.get<KeyValue<number, string>[]>(url, {
            params: {
                getLocums: getLocums.toString()
            }
        });
    }

    // ** DocumentAdminController.GetStates
    getStates(): Observable<KeyValue<number, string>[]> {
        const url = `${this.coreApiAdminPath}/states`;
        return this.http.get<KeyValue<number, string>[]>(url);
    }

    // ** DocumentAdminController.GetRequirementLevels
    getRequirementLevels(): Observable<KeyValue<number, string>[]> {
        const url = `${this.coreApiAdminPath}/requirement-levels`;
        return this.http.get<KeyValue<number, string>[]>(url);
    }

    // ** DocumentTypesController.GetAllDocumentTypeCategories or DocumentTypesController.GetDocumentTypeCategoriesByLobAsync
    getDocumentTypesCategories(clob: CategoryLineOfBusiness): Observable<DocumentTypeCategory[] | string[]> {
        let url;
        if (clob === 0) {
            url = combine(this.coreApiDocTypesPath, 'categories-all');
            return this.http.get<string[]>(url);
        }
        url = combine(this.coreApiDocTypesPath, 'categories');

        return this.http.get<DocumentTypeCategory[]>(url, {
            params: {
                clob: clob.toString()
            }
        });
    }

    // ** DocumentAdminController.GetDocumentAdmin
    getDocumentTypes(args: FetchArgs): Observable<PagingTokenFixed<DocumentTypeSummaryBase[]>> {
        const url = `${this.coreApiAdminPath}`;
        const params = {};
        buildHttpParams(params, args, '');
        return this.http.get<PagingTokenFixed<DocumentTypeSummaryBase[]>>(url, { params });
    }

    // ** DocumentAdminController.IsOcrEnabled
    getEnableOcrStatus(): Observable<boolean> {
        const url = combine(this.coreApiAdminPath, 'ocr-enabled');
        return this.http.get<boolean>(url);
    }

    // ** DocumentAdminController.ToggleOcrEnabled
    toggleOcrStatus(): Observable<any> {
        const url = combine(this.coreApiAdminPath, 'toggle-ocr');
        return this.http.get(url);
    }

    // ** DocumentAdminController.DownloadFile
    downloadDocumentBlobById(documentId: number): Observable<Blob> {
        const url = `${this.coreApiAdminPath}/download-file/${documentId}`;
        return this.http.get(url, { responseType: 'blob' });
    }

    // ** DocumentAdminController.GetDocumentTypeAsync
    getFacilitySpecificDocument(id: number): Observable<DocumentTypes> {
        const url = `${this.coreApiAdminPath}/${id}`;
        return this.http.get<DocumentTypes>(url);
    }

    // ** DocumentAdminController.InitializeDocumentTypeAsync
    initializeDocument(): Observable<DocumentTypes> {
        const url = `${this.coreApiAdminPath}/initialize`;
        return this.http.get<DocumentTypes>(url);
    }

    // ** DocumentAdminController.DownloadFile
    downloadExampleDocument(documentId: number): Observable<Blob> {
        const url = `${this.coreApiAdminPath}/download-file/${documentId}`;
        return this.http.get(url, { responseType: 'blob' });
    }

    // ** DocumentAdminController.CreatePdf
    createNewDocument(id: number, pages: number[]): Observable<DocumentTypes> {
        const url = `${this.coreApiAdminPath}/${id}/create-pdf`;

        return this.http.post<DocumentTypes>(url, pages);
    }

    // ** DocumentAdminController.RemoveRequirement
    removeRequirement(id: number): Observable<DocumentTypes> {
        const url = `${this.coreApiAdminPath}/${id}/remove-requirement`;

        return this.http.delete<DocumentTypes>(url);
    }

    // ** Does not exist.
    getDocumentTypesList(name: string = null): Observable<DocumentType[]> {
        const params = interpolateParams({
            lineOfBusiness: LineOfBusiness.PerDiem,
            name: name
        });
        return this.http.get<DocumentType[]>(this.coreApiDocTypesPath, { params });
    }

    // ** DocumentTypesController.CreateDocumentTypeAsync
    createDocumentType(docType: DocumentType): Observable<DocumentType> {
        const url = `${this.coreApiDocTypesPath}`;
        return this.http.post<DocumentType>(url, docType);
    }

    // ** DocumentTypesController.AddDocumentTypeCodeSetAsync
    addSubType(name: string, id: number): Observable<unknown> {
        const url = `${this.coreApiDocTypesPath}/${id}/codesets`;
        return this.http.post<unknown>(url, { name });
    }

    // ** DocumentTypesController.AddDocumentTypeCodesAsync
    addDocCode(newDocCode: unknown, id: number): Observable<unknown> {
        const url = `${this.coreApiDocTypesPath}/${id}/codes`;
        return this.http.post<unknown>(url, newDocCode);
    }

    // ** DocumentTypesController.UpdateDocumentTypeCodeAsync
    editDocCode(newDocCode: unknown, id: number, codeId: number): Observable<unknown> {
        const url = `${this.coreApiDocTypesPath}/${id}/codes/${codeId}`;
        return this.http.put<unknown>(url, newDocCode);
    }

    // ** DocumentTypesController.RemoveDocumentTypeCodeAsync
    deleteDocCode(id: number, codeId: number): Observable<unknown> {
        const url = `${this.coreApiDocTypesPath}/${id}/codes/${codeId}`;
        return this.http.delete<unknown>(url);
    }

    // ** DocumentTypesController.UpdateDocumentTypeAsync
    updateDocumentType(docType: DocumentType, id: number): Observable<DocumentType> {
        const url = `${this.coreApiDocTypesPath}/${id}`;
        return this.http.put<DocumentType>(url, docType);
    }

    // ** DocumentTypesController.GetDocumentTypeByIdAsync
    getDocumentType(id: number): Observable<DocumentType> {
        const url = `${this.coreApiDocTypesPath}/${id}`;
        return this.http.get<DocumentType>(url);
    }

    // ** Does not exist.
    uploadSupplementalFile(file: File): Observable<unknown> {
        const url = `${this.coreApiDocTypesPath}/UploadFile`;
        const data = new FormData();
        data.append('file', file);
        return this.http.post<unknown>(url, data);
    }

    // ** DocumentAdminController.GetProfessionTreeAsync
    getProfessionTree(): Observable<ProfessionTree> {
        const url = `${this.coreApiAdminPath}/profession-tree`;
        return this.http.get<ProfessionTree>(url);
    }
}
